import { useParams, Navigate, Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import { Loading } from "src/components/utils/Loading";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { CompanyMetadataProvider } from "src/context/Company/Metadata/provider";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import CompanyScoreContextProvider from "src/pages/Company/Scores/CompanyScoreContextProvider";
import { URLHandler } from "src/services/URLHandler";
import { SimpleLayout } from "src/templates/layouts/SimpleLayout";
import { WhiteLayout } from "src/templates/layouts/WhiteLayout";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { getSsqDisplayTitle } from "src/utils/ssqTitle";
import { CompanyOverviewProvider } from "src/context/Company/Overview/provider";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useCompanyOverviewContext } from "src/context/Company/Overview/useCompanyOverviewContext";
import { VariationSelector } from "src/components/Selector/VariationSelector/VariationSelector";

/**
 * NOTE — These components are extracted out of `companyRouter.tsx` so the
 * router file only exports a single hook-like factory (`useCompanyRouter`),
 * keeping it compatible with Vite React Fast Refresh's "consistent components
 * exports" rule. Mixing component declarations with a `use*` export in the same
 * module breaks HMR.
 *
 * See https://github.com/vitejs/vite-plugin-react-swc#consistent-components-exports
 */
export const RedirectInsideScores = () => {
    const { getEntityScore } = useEntityScores();
    const { scoreId } = useParams();

    const scoreDetail = getEntityScore(Number(scoreId));

    if (!scoreDetail) return <Loading />;

    if (scoreDetail.displayMode === "regulation") {
        return <Navigate to={"./regulatory"} replace />;
    }

    return <Navigate to={"./distribution"} replace />;
};

export const RedirectToOverview = () => {
    const { company } = useCompanyMetadata();
    const url = URLHandler.company.overview(company.id);

    return <Navigate to={url} replace />;
};

export const RedirectToHeadings = () => {
    const { headingId } = useParams();

    return <Navigate to={`../headings/${headingId}`} replace />;
};

export const CompanyOverviewContext = () => {
    const { ssqId } = useParams();

    return (
        <CompanyOverviewProvider ssqId={ssqId}>
            <Outlet />
        </CompanyOverviewProvider>
    );
};

export const CompanyOverviewLayout = () => {
    const { currentCampaign } = useFoodpilotContext();
    const { t } = useTranslation();

    return (
        <SimpleLayout
            title={t("company.overview.title", "My Company")}
            subtitle={t("company.overview.subtitle", `in {{campaign}}`, { campaign: currentCampaign?.label ?? "" })}
        >
            <Outlet />
        </SimpleLayout>
    );
};

export const CompanyOneOverviewLayout = () => {
    const { currentCampaign } = useFoodpilotContext();
    const { selectedVariations, allScoresWithVariations } = useEntityScores();
    const { filteredOverview } = useCompanyOverviewContext();
    const { t } = useTranslation();

    const isOneScorePage = filteredOverview !== null && filteredOverview.scores.length === 1;
    const scoreId = filteredOverview?.scores.find((i) => true)?.id;
    const variationsForScore = allScoresWithVariations.filter((variation) => variation.scoreId === scoreId);
    const selectedVariation = scoreId === undefined ? undefined : selectedVariations.state.variations?.[scoreId];
    const selectedScore = variationsForScore.find((i) => {
        if (!selectedVariation) {
            return i.variationId === null;
        }
        return i.variationId === selectedVariation;
    });

    return (
        <SimpleLayout
            title={t("company.overview.title", "My Company")}
            subtitle={t("company.overview.subtitle", `in {{campaign}}`, { campaign: currentCampaign?.label ?? "" })}
            selectors={
                <Stack flexDirection={"row"}>
                    {isOneScorePage && (
                        <VariationSelector
                            value={selectedScore ?? null}
                            variations={variationsForScore}
                            onChange={(scoreWithVariation) => {
                                if (scoreWithVariation === null) return;

                                selectedVariations.state.selectVariation(
                                    scoreWithVariation.scoreId,
                                    scoreWithVariation.variationId,
                                );
                            }}
                        />
                    )}
                </Stack>
            }
        >
            <Outlet />
        </SimpleLayout>
    );
};

export const CompanyQuestionnaireLayout = () => {
    const { getEntityScore } = useEntityScores();
    const { company, ssqVersion } = useCompanyMetadata();
    const { scoreId } = useParams();
    const { t } = useTranslation();

    const scoreDetail = getEntityScore(Number(scoreId));
    const scoreIdAsNumber = scoreId === undefined ? undefined : Number(scoreId);

    if (!scoreDetail) return <Loading />;

    const url =
        ssqVersion ?
            URLHandler.company.overviewSsq(company.id, ssqVersion.ssq.id, scoreIdAsNumber)
        :   URLHandler.company.overview(company.id);

    const title = getSsqDisplayTitle(ssqVersion?.ssq);
    return (
        <WhiteLayout
            title={`${title}`}
            chipContent={ssqVersion ? `v${ssqVersion.versionMajor}.${ssqVersion.versionMinor}` : undefined}
            subtitleLink={{
                label: t("company.dashboard.go_back_to_dashboard", "Go back to dashboard"),
                goTo: url,
            }}
        >
            <Outlet />
        </WhiteLayout>
    );
};

export const CompanyMetadataContext = () => {
    const { selectedCompany } = useFoodpilotContext();

    if (selectedCompany === null) {
        return <Loading />;
    }

    return (
        <CompanyMetadataProvider companyId={selectedCompany.id}>
            <Outlet />
        </CompanyMetadataProvider>
    );
};

export const CompanyScoresContext = () => {
    const { selectedCompany } = useFoodpilotContext();
    const { ssqId } = useParams<{ ssqId: string }>();

    const ssqIdNumber = ssqId ? parseInt(ssqId) : undefined;

    if (selectedCompany === null) {
        return <Loading />;
    }

    return (
        <CompanyMetadataProvider companyId={selectedCompany.id} ssqId={ssqIdNumber}>
            <CompanyWithSsqLayout />
        </CompanyMetadataProvider>
    );
};

const CompanyWithSsqLayout = () => {
    const { ssqVersion } = useCompanyMetadata();

    return (
        <EntityScoresProvider entity="company" ssq={ssqVersion?.ssq}>
            <Outlet />
        </EntityScoresProvider>
    );
};

export const CompanyScoreDetailContext = () => {
    return (
        <CompanyScoreContextProvider>
            <Outlet />
        </CompanyScoreContextProvider>
    );
};
